// Added by Jonghyung Lee on 2021-12-14
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useState } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'

// helper
import Intl from '@/helper/Intl'
import { getDateWithFormat } from '@/helper/util'
import { WPSWorks } from '@/helper/define/wpsDefine'
import Logger from '@/helper/Logger'

// api
import { reqCancelWPSWorks } from '@/service/wps.service'

// ui
import { ConfirmDialog } from '@/components/common/v2/Dialog/ConfirmDialog'
import { Button } from '@/components/worklist/Button'

///////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface WPSMonitorignDetailTableProps {
  tableTitle?: string
  tasks?: WPSWorks[]
  isWaiting?: boolean
  proxyToken?: string | null
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const WPSMonitorignDetailTable = ({
  tableTitle,
  tasks = [], // running 우선, waiting 나중
  isWaiting = false,
  proxyToken,
  ...rest
}: WPSMonitorignDetailTableProps) => {
  const classes = useStyles()
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)
  const [cancelCandidate, setCancelCandidate] = useState<string>('')

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // i18n
  const btnCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.btn_cancel' })
  const colCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.col_cancel' })
  const colCreateAt = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.col_created_at' })
  const colPatientInfo = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.col_patient_info' })
  const colStartAt = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.col_started_at' })
  const colStatus = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.col_status' })
  const colTaskName = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.col_task_name' })
  const titleDialogCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.dialog_title_cancel' })
  const msgConfirmCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.msg_confirm_cancel' })
  const unknown = Intl.getIntlText({ id: 'htmljs_react.worklist.wpsmonitoringdetailtable.unknown' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // handler
  const handleClickTaskCancelBtn = (id: string) => {
    setConfirmDialogOpen(true)
    setCancelCandidate(id)
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setCancelCandidate('')
  }

  const handleConfirmDialogConfirm = async () => {
    if (!proxyToken) return
    try {
      await reqCancelWPSWorks(cancelCandidate)
    } catch (error) {
      Logger.errLog(`Can't cancel Process because: ${error}`)
    } finally {
      handleConfirmDialogClose()
    }
  }

  return (
    <div className={classes.root}>
      <p className={classes.tableTitle}>{tableTitle}</p>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <colgroup>
            {/* process name */}
            <col style={{ width: '15%' }} />
            {/* status */}
            <col style={{ width: '10%' }} />
            {/* patient info */}
            <col style={{ width: '25%' }} />
            {/* created at */}
            <col style={{ width: '15%' }} />
            {/* started at */}
            <col style={{ width: '15%' }} />
            {/* cancel btn */}
            {<col style={{ width: '8%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th>{colTaskName}</th>
              <th>{colStatus}</th>
              <th>{colPatientInfo}</th>
              <th>{colCreateAt}</th>
              <th>{colStartAt}</th>
              <th>{colCancel}</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length > 0 &&
              tasks.map(({ id, created_at, proc_started_at, status, type, patient_names, patient_ids }) => (
                <tr key={`${id}_${status}`}>
                  <td key={`type_${type}`}>{type}</td>
                  <td key={`status_${status}`}>{status}</td>
                  <td key={`info`}>
                    {patient_ids.split(',')[0] || unknown} ({patient_names.split(',')[0] || unknown}) Total:{' '}
                    {patient_ids.split(',').length}
                  </td>
                  <td key={`create_${created_at}`}>{getDateWithFormat(created_at)}</td>
                  <td key={`start_${proc_started_at}`}>{getDateWithFormat(proc_started_at)}</td>
                  <td key={`cancel_${status}`}>
                    <Button type='cancel' onBtnClick={() => handleClickTaskCancelBtn(id)}>
                      {btnCancel}
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        title={titleDialogCancel}
        open={confirmDialogOpen}
        onCloseBtnClick={handleConfirmDialogClose}
        onConfirmBtnClick={handleConfirmDialogConfirm}
        onCancelBtnClick={handleConfirmDialogClose}
        messages={msgConfirmCancel}
        hasCancelBtn
      />
    </div>
  )
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    tableWrapper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      height: '200px',
      background: '#000',
      border: '1px solid #000',
      overflow: 'hidden auto',
      '&::-webkit-scrollbar-thumb': {
        width: '50px',
        height: '50px',
        borderRadius: '4px',
        background: '#646464',
      },
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
    },
    tableTitle: {
      fontWeight: 'bold',
      color: '#fff',
    },
    table: {
      border: 0,
      borderCollapse: 'separate',
      borderSpacing: 0,
      '& th': {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: '#383838',
        borderBottom: '1px solid #000',
        paddingLeft: '8px',
        textAlign: 'left',
        '&:before': {
          content: '""',
          position: 'absolute',
          background: '#f00',
          right: 0,
          top: '15%',
          bottom: '15%',
          width: '2px',
          backgroundColor: '#2c2d2f',
        },
        '&:last-of-type:before': {
          content: 'none',
        },
      },
      '& tr': {
        height: '42px',
        backgroundColor: '#1B1B1B',
        boxSizing: 'border-box',
      },
      '& > thead': {
        color: '#e6e6e6',
        top: 0,
        '& > tr': {
          height: '31px',
        },
      },
      '& > tbody': {
        '& > tr': {
          color: '#b4b4b4',
          '&:not(:first-child) > td': {
            borderTop: '1px solid #383838',
          },
        },
        '& td': {
          paddingLeft: '8px',
        },
      },
    },
  })
)

export default WPSMonitorignDetailTable
